/**
 * Local storage utilities for managing favorite well lists for type wells
 * Each type well (forecast) can have one favorite well list marked with a star
 */

const FAVORITE_WELL_LIST_PREFIX = "typewell-favorite-welllist-";

/**
 * Event name constants for favorite well list synchronization
 */
export const FAVORITE_WELL_LIST_EVENTS = {
  FAVORITE_CHANGED: "typewell-favorite-changed",
  STORAGE: "storage"
} as const;

/**
 * Dispatch a custom event to notify other components about favorite changes
 */
function dispatchFavoriteChangeEvent(forecastId: string, changeId: string | null): void {
  const event = new CustomEvent(FAVORITE_WELL_LIST_EVENTS.FAVORITE_CHANGED, {
    detail: { forecastId, changeId }
  });
  window.dispatchEvent(event);
}

/**
 * Get the favorite well list ID for a specific type well
 * @param forecastId - The type well (forecast) ID
 * @returns The changeId of the favorite well list, or null if no favorite set
 */
export function getFavoriteWellListId(forecastId: string): string | null {
  if (!forecastId) return null;

  const key = `${FAVORITE_WELL_LIST_PREFIX}${forecastId}`;
  return localStorage.getItem(key);
}

/**
 * Set the favorite well list for a specific type well
 * @param forecastId - The type well (forecast) ID
 * @param changeId - The changeId of the well list to mark as favorite
 */
export function setFavoriteWellListId(forecastId: string, changeId: string): void {
  if (!forecastId || !changeId) return;

  const key = `${FAVORITE_WELL_LIST_PREFIX}${forecastId}`;
  localStorage.setItem(key, changeId);

  // Notify other components
  dispatchFavoriteChangeEvent(forecastId, changeId);
}

/**
 * Remove the favorite well list for a specific type well
 * @param forecastId - The type well (forecast) ID
 */
export function removeFavoriteWellListId(forecastId: string): void {
  if (!forecastId) return;

  const key = `${FAVORITE_WELL_LIST_PREFIX}${forecastId}`;
  localStorage.removeItem(key);

  // Notify other components
  dispatchFavoriteChangeEvent(forecastId, null);
}
